/**
 * The Money tab (P1-TR3).
 *
 * Three things a partner does with money on a phone — record it, look at it,
 * approve someone else's — behind three tabs rather than three navigation
 * levels. On a phone, depth costs more than breadth: every level down is a
 * back button somebody has to find.
 *
 * Recording is the default tab because it is the one done standing in a shop,
 * and the one where the sixty-second budget in §9.5 applies.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { ApprovalQueue } from './ApprovalQueue.js';
import { ExpenseForm } from './ExpenseForm.js';
import { TransactionList } from './TransactionList.js';
import { useTransactions } from './api.js';

const TABS = ['record', 'list', 'approvals'] as const;
type Tab = (typeof TABS)[number];

export function MoneyPage(): JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('record');

  // The badge is what makes the queue get looked at. Without a count on the
  // tab, an entry can sit pending for a week and nobody is at fault.
  const { data: pending } = useTransactions({ status: 'pending', limit: 1 });

  return (
    <div className="bg-gray-50">
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white" role="tablist">
        <div className="mx-auto flex max-w-md">
          {TABS.map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={tab === option}
              onClick={() => setTab(option)}
              className={`min-h-touch flex-1 px-2 py-3 text-sm ${
                tab === option ? 'border-b-2 border-green-700 font-medium text-green-800' : 'text-gray-600'
              }`}
            >
              {t(`finance.tab.${option}`)}
              {option === 'approvals' && pending !== undefined && pending.total > 0 && (
                <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-xs text-amber-900">{pending.total}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {tab === 'record' && <ExpenseForm onRecorded={() => setTab('list')} />}
      {tab === 'list' && <TransactionList />}
      {tab === 'approvals' && <ApprovalQueue />}
    </div>
  );
}
